import styled from 'styled-components';

import {dimensions} from '@stryd/react-ui';

import {BackgroundNextImage} from './BackgroundNextImage';
import {InternalLink} from './InternalLink';

const PlainLink = styled(InternalLink)`
  &:hover {
    color: inherit;
    text-decoration: none;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: ${dimensions.twicePadding};

  width: 100%;

  @media screen and (max-width: ${dimensions.breakpoints.tabletLandscape}) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media screen and (max-width: ${dimensions.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const BackgroundOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  background-color: white;
  opacity: 0;
  transition: opacity 0.2s;

  z-index: 0;
`;

const mobileTextContainerHeight = '100px';

const GridItemTextContainer = styled.div`
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s, visibility 0s;

  width: 100%;
  height: 100%;
  padding: ${dimensions.defaultPadding};
  position: absolute;
  top: 0;
  left: 0;

  z-index: 1;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @media screen and (max-width: ${dimensions.breakpoints.tabletLandscape}) {
    visibility: visible;
    opacity: 0.99;
    height: ${mobileTextContainerHeight};
    background-color: white;
    top: unset;
    bottom: 0;
    overflow: hidden;
    padding: ${dimensions.twicePadding};
  }
`;

const BackgroundImageContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;

  @media screen and (max-width: ${dimensions.breakpoints.tabletLandscape}) {
    position: absolute;
    top: calc(-1 * ${mobileTextContainerHeight} / 2);
    left: 0;
  }
`;

const BackgroundImageContainerInner = styled.div`
  width: 100%;
  height: 100%;

  transform: scale(1);
  transition: transform 0.2s;
`;

const GridItemContainer = styled.div`
  grid-column: span 1;
  grid-row: span 1;

  position: relative;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  width: 100%;
  padding-top: 100%;
  border-radius: ${dimensions.defaultBorderRadius};

  z-index: 1;

  overflow: hidden;

  &:hover {
    ${GridItemTextContainer} {
      visibility: visible;
      opacity: 0.99;
    }

    ${BackgroundOverlay} {
      opacity: 0.8;
    }

    ${BackgroundImageContainerInner} {
      transform: scale(1.05);
    }
  }

  @media screen and (max-width: ${dimensions.breakpoints.tabletLandscape}) {
    &:hover {
      ${BackgroundOverlay} {
        opacity: 0;
      }
    }
  }
`;

const GridItemPositionContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;

  width: 100%;
  height: 100%;
`;

const GridItemInnerContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const GridTitleContainer = styled.div`
  grid-column: span 2;
  grid-row: span 2;
  display: flex;
  align-items: center;
  justify-content: center;

  @media screen and (max-width: ${dimensions.breakpoints.tabletLandscape}) {
    min-height: 40vh;
  }

  @media screen and (max-width: ${dimensions.breakpoints.mobile}) {
    grid-column: span 1;
  }
`;

export const GridTitle = styled.h1`
  font-size: 3rem;

  &:after {
    content: '';
    display: block;
    margin-top: ${dimensions.twicePadding};
    width: 25%;
    height: 5px;
    background-color: black;
  }
`;

const GridItemTitle = styled.h2`
  font-size: 1.2rem;
  margin-bottom: 0.75rem;
  text-align: center;
  max-width: 100%;
`;

const GridItemSubtitle = styled.em`
  font-size: 1rem;
`;

export interface GridItem {
  key: string;
  title: React.ReactNode;
  subTitle?: React.ReactNode;
  backgroundImageSrc: string;
  linkTo: string;
}

interface LinkGridLayoutProps {
  gridTitle: React.ReactNode;
  items: GridItem[];
}

export const LinkGridLayout: React.FC<LinkGridLayoutProps> = (props) => {
  const {gridTitle, items} = props;

  return (
    <Grid>
      <GridTitleContainer>
        {typeof gridTitle === 'string' ? (
          <GridTitle>{gridTitle}</GridTitle>
        ) : (
          gridTitle
        )}
      </GridTitleContainer>

      {items.map((item) => (
        <PlainLink key={item.key} href={item.linkTo}>
          <GridItemContainer>
            <GridItemPositionContainer>
              <GridItemInnerContainer>
                {item.backgroundImageSrc && (
                  <BackgroundImageContainer>
                    <BackgroundImageContainerInner>
                      <BackgroundNextImage
                        imageProps={{
                          src: item.backgroundImageSrc,
                          alt: `Photo of ${item.title}`,
                        }}
                      />
                    </BackgroundImageContainerInner>
                  </BackgroundImageContainer>
                )}

                <BackgroundOverlay />

                <GridItemTextContainer>
                  {typeof item.title === 'string' ? (
                    <GridItemTitle>{item.title}</GridItemTitle>
                  ) : (
                    item.title
                  )}

                  {typeof item.subTitle === 'string' ? (
                    <GridItemSubtitle>{item.subTitle}</GridItemSubtitle>
                  ) : (
                    item.subTitle
                  )}
                </GridItemTextContainer>
              </GridItemInnerContainer>
            </GridItemPositionContainer>
          </GridItemContainer>
        </PlainLink>
      ))}
    </Grid>
  );
};
